<style>
  .btn-warning-ish{
    color: #000000;
    background-color: #faf5e3;
    border-color: #eae3cf;
  }
  .btn-success-ish{
    color: #000;
    background-color: #d9f1e2;
    border-color: #c9e1d2;
  }
  .btn-danger-ish{
    background-color: #ffcccc;
    border-color: #e6adad;
    color: #000;
  }

  .transaction-preview-table{
    background: #f4f4f4 !important;
  }

  .transaction-preview-table thead th{
    border-bottom-color: #dddddd;
    font-weight: normal;
    text-transform: uppercase;
    font-size: 1em;
    color: #6a6969;
    letter-spacing: 0.03em;
  }

  .transaction-preview-table thead th,
  .transaction-preview-table tbody td{
    border-right: 1px solid #dddddd;
  }

  .transaction-preview-table thead th:first-child,
  .transaction-preview-table tbody td:first-child{
    border-left: 1px solid #dddddd;
  }

  .transaction-preview-table tbody td{
    border-bottom: 1px solid #dddddd;
  }

  #approveWithdrawForm .modal-body{
    padding-bottom: 0;
  }
</style>

<template>
  <div>
    <div class="card">
      <div class="pl-4 card-header d-flex align-items-center justify-content-between">
        <h5 class="mb-0">Withdraw Requests</h5>
      </div>

      <div class="card-body" v-if="fetchingTransactions || !transactions || !transactions.length">
        <Loader :size="90" v-if="fetchingTransactions" />

        <div class="text-center" v-else-if="!transactions">
          Failed to fetch withdraw requests
        </div>

        <div class="text-center" v-else-if="!transactions.length">
          No withdraw requests found
        </div>
      </div>

      <table v-else class="table table-stripped" style="border-top: none !important">
        <thead>
          <tr>
            <th style="paddingLeft: 1.7em">Source Transaction Id</th>
            <th>From Account</th>
            <th>To Account</th>
            <th class="text-center">Request Date</th>
            <th class="text-center">Amount</th>
            <th class="text-center">Available Balance</th>
            <th class="text-center">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr class="text-capitalize" v-for="(transaction, index) in transactions" :key="index">
            <td style="paddingLeft: 1.7em">{{ transaction.reference || "Not provided" }}</td>
            <td>
              {{ transaction.fromAccount ? transaction.fromAccount.name : transaction.fromExternalAccount || "Unknown" }}
            </td>
            <td>{{ transaction.toAccount ? transaction.toAccount.name : transaction.toExternalAccount }}</td>
            <td class="text-center">{{ transaction.date }}</td>
            <td class="text-center"> {{ transaction.amount ? parseInt(transaction.amount).toLocaleString() : "" }} </td>
            <td class="text-center"> 
              {{ transaction.fromAccount && transaction.fromAccount.availableBalance ? parseInt(transaction.fromAccount.availableBalance).toLocaleString() : "Unknown" }} 
            </td>
            
            <td class="text-center">
              <span v-if="transaction.approvalStatus">
                {{ transaction.approvalStatus == 1 ? 'Approved' : 'Rejected' }}
              </span>

              <template v-else>
                <button class="btn btn-sm btn-primary" style="flex-shrink: 0"
                  v-has-permissions="[PERMISSIONS.APPROVE_WITHDRAW]"
                  @click="openApproveWithdraw(transaction._id)">
                  <i class="fa fa-check"></i>&nbsp;
                  Approve&nbsp;
                </button>
                &nbsp;

                <button class="btn btn-sm btn-danger" style="flex-shrink: 0"
                  v-has-permissions="[PERMISSIONS.REJECT_WITHDRAW]"
                  @click="openRejectWithdraw(transaction._id)">
                  <i class="fa fa-close"></i> &nbsp;
                  Reject&nbsp;
                </button>

                <span v-has-no-permissions="[`${PERMISSIONS.APPROVE_WITHDRAW}|${PERMISSIONS.REJECT_WITHDRAW}`]">
                  Pending
                </span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="card-body py-0" v-if="withdrawPagination && !fetchingTransactions">
        <b-pagination
          :value="withdrawPagination.currentPage"
          @change="fetchTransactions($event)"
          :total-rows="withdrawPagination.totalRows"
        />
      </div>
    </div>

    <router-view></router-view>

    <form id="approveWithdrawForm" @submit="approveWithdraw">
      <b-modal title="Approve Withdraw" :visible="transactionBeingApproved !== null" @ok="approveWithdraw" @cancel="transactionBeingApprovedId = null" @hide="transactionBeingApprovedId = null">
        <div class="form-group">
          <label for="referenceNumber">Reference Number</label>
          <input
            id="referenceNumber"
            type="text"
            class="form-control"
            required
            v-model="referenceNumber"
          />
        </div>

        <template slot="modal-footer">
          <div class="row justify-content-end">
            <div class="px-3">
              <button @click="approveWithdraw"
                :class="{disabled: approvingWithdraw}"
                class="btn btn-primary px-4"
              >{{ !approvingWithdraw ? 'APPROVE' : 'APPROVING...' }}</button>
            </div>
          </div>
        </template>
      </b-modal>
    </form>

    <form id="rejectWithdrawForm" @submit="rejectWithdraw">
      <b-modal title="Reject Withdraw" :visible="transactionBeingRejected !== null" @ok="rejectWithdraw" @cancel="transactionBeingRejectedId = null" @hide="transactionBeingRejectedId = null">
        <div class="form-group">
          <label for="reason">Rejection Reason</label>
          <textarea class="form-control" required
            v-model="reason" rows="3"></textarea>
        </div>

        <template slot="modal-footer">
          <div class="row justify-content-end">
            <div class="px-3">
              <button type="submit"
                :class="{disabled: rejectingWithdraw}"
                class="btn btn-primary px-4"
              >{{ !rejectingWithdraw ? 'SUBMIT' : 'REJECTING...' }}</button>
            </div>
          </div>
        </template>
      </b-modal>
    </form>
  </div>
</template>

<script>
  import formatDate from 'date-fns/format';
  import { PERMISSIONS } from '@/permissions';
  import { fetchWithdraws, submitWithdrawApproval, submitWithdrawRejection } from '@/services/API';
  import { showErrorToast, showSuccessToast } from '@/utils';

  export default {
    name: 'Withdraws',
    data(){
      return {
        PERMISSIONS,
        approvingWithdraw: false,
        rejectingWithdraw: false,
        transactionBeingApprovedId: null,
        transactionBeingRejectedId: null,
        showEnterRejectRemark: true,
        referenceNumber: "",
        reason: "",
        transactions: null, 
        withdrawPagination: null,
        fetchingTransactions: false
      }
    },
    mounted(){
      this.fetchTransactions();
    },
    computed: {
      transactionBeingApproved: function(){
        if(!this.transactions || !this.transactionBeingApprovedId)
          return null;

        return this.transactions.find(t => t._id === this.transactionBeingApprovedId);
      },
      transactionBeingRejected: function(){
        if(!this.transactions || !this.transactionBeingRejectedId)
          return null;

        return this.transactions.find(t => t._id === this.transactionBeingRejectedId);
      },
      submittingRejection: function(){
        if(!this.transactionBeingApproved)
          return false;

        return this.transactionBeingApproved.rejecting;
      }
    },
    methods: {
      async fetchTransactions(page = 1){
        try {
          this.fetchingTransactions = true;
          const {data, pagination} = await fetchWithdraws(page);
          this.transactions = data.map(transaction => {
            if(transaction.accountTransactions && transaction.accountTransactions.length){
              const accountTransactionDetails = transaction.accountTransactions[0];
              if(accountTransactionDetails){
                const { fromAccount, toAccount, toExternalAccount } = accountTransactionDetails;
                transaction = {
                  ...transaction, fromAccount, toAccount, toExternalAccount
                }
              }
            }

            return {
              ...transaction,
              date: formatDate(transaction.createdAt, 'Do MMM YYYY')
            };
          }); 
          this.withdrawPagination = pagination;
          this.fetchingTransactions = false;
        } catch (error) {
          this.fetchingTransactions = false;
        }
      },
      openApproveWithdraw(id){
        this.transactionBeingApprovedId = id;
      },
      async approveWithdraw(e){
        if(e) e.preventDefault();

        try {
          this.approvingWithdraw = true;
          await submitWithdrawApproval(this.transactionBeingApprovedId, this.referenceNumber);

          this.transactions = this.transactions.map(transaction => {
            if(transaction._id === this.transactionBeingApprovedId)
              return {...transaction, approvalStatus: 1};

            return transaction;
          });

          this.$nextTick(() => {
            this.referenceNumber = "";
            this.approvingWithdraw = false;
            this.transactionBeingApprovedId = null;

            showSuccessToast("Withdraw approved");
          });
        } catch (error) {
          showErrorToast("Couldn't approve withdraw");
          this.approvingWithdraw = false;
        }
      },
      openRejectWithdraw(id){
        this.transactionBeingRejectedId = id;
      },
      async rejectWithdraw(e){
        if(e) e.preventDefault();

        try {
          this.rejectingWithdraw = true;
          await { then: resolve => setTimeout(resolve, 2000) };
          await submitWithdrawRejection(this.transactionBeingRejectedId, this.reason);

          this.transactions = this.transactions.map(transaction => {
            if(transaction._id === this.transactionBeingRejectedId)
              return {...transaction, approvalStatus: 2};

            return transaction;
          });

          this.$nextTick(() => {
            this.referenceNumber = "";
            this.rejectingWithdraw = false;
            this.transactionBeingRejectedId = null;

            showSuccessToast("Withdraw rejected");
          });
        } catch (error) {
          showErrorToast("Couldn't reject withdraw");
          this.rejectingWithdraw = false;
        }
      }
    }
  }
</script>
